import type {
  IOrderConfirmationConfig,
  SessionDataArgs,
} from '../graphQL/queries/getSession/types'
import type {
  IOrderDetail,
  OrderExternalRefStatus,
} from '../graphQL/queries/order/types'
import {
  ORDER_POLL_FIRST_LOOK_MS,
  ORDER_POLL_INTERVAL_MS,
  ORDER_POLL_MAX_TIMEOUT_MS,
  ORDER_POLL_MIN_INTERVAL_MS,
  ORDER_POLL_TIMEOUT_MS,
} from '../redux/slices/orders/types'

/**
 * Every member of the backend's `OrderStatus` enum.
 *
 * Hand-mirrored rather than imported, for the reason
 * `ISessionOrderFilter.status` gives: cinnamon cannot reach the storefront's
 * `@engyalo/storefront-commons` types. Kept here so a configured status can be
 * checked against something, because the alternative is that a typo is only
 * ever observable as an order that polls to its deadline and then reports a
 * timeout it never had.
 *
 * Order follows the enum declaration, which is alphabetical apart from the two
 * trailing members.
 * @internal
 */
export const ORDER_STATUSES = [
  'CANCELLED',
  'CONFIRMED',
  'CREATED',
  'DELIVERED',
  'EDITED',
  'ERROR',
  'IN_PROGRESS',
  'PAID',
  'PROCESSED',
  'SENT',
  'SHIPPED',
  'SUGGESTED',
  'WAITING_CALLBACK',
] as const

/**
 * The confirmation parameters as the poll actually uses them: every field
 * present, every number usable.
 * @internal
 */
export interface IResolvedOrderConfirmation {
  /**
   * True only when the storefront declared `required: true`.
   *
   * Two effects, and it is worth keeping them apart. It turns the Order Detail
   * screen's `pending` and `timeout` states on — the *visible* confirming
   * affordance, meaningless where nothing confirms asynchronously. And it
   * decides what a bare `CREATED` order means to `deriveOrderOutcome`:
   * terminal (`created`) where the storefront declared no asynchronous
   * confirmation, still in flight (`pending`) where it did.
   *
   * What it does **not** do is suppress the poll for an order that reports
   * itself still in progress. `externalRefStatus: PROCESSING` and
   * `WAITING_CALLBACK` are re-read either way, because the alternative is a
   * screen showing a status the server has already moved on from.
   */
  isAsync: boolean
  /** Milliseconds between poll ticks. */
  pollingTime: number
  /**
   * Maximum number of poll ticks.
   *
   * **Derived from `timeout` and `pollingTime`, never merely read** (COM-871).
   * It used to default to `Infinity`, reproducing COM-741's wall-clock-only
   * bound, and an authored value was taken at face value however unreachable
   * it was. Both directions were silently inconsistent with the deadline: a
   * `maxAttempts` above what the budget affords is dead configuration, and one
   * below it is the bound nobody reads. `derivePollAttempts` is now the
   * ceiling *and* the default, and an authored value can only tighten it — see
   * that function for the arithmetic and for what a slow request does to it.
   *
   * Never `Infinity`, and never large enough to let a tick be **scheduled**
   * after the deadline has passed — nor, since the deadline itself is capped,
   * larger than `ORDER_POLL_MAX_TIMEOUT_MS` affords at the interval floor,
   * which is 601.
   *
   * Scheduled, not issued — but the gap is now narrow, and naming it wrongly
   * is worse than leaving it. Request latency is **not** the mechanism: since
   * COM-871 `elapsed` is sampled after the preceding request resolves and the
   * delay is capped at `timeout - elapsed`, so however slow that request was,
   * the next one is issued at or before `timeout` (`nextPollDelay`). What
   * remains is timer lateness — a browser fires a `setTimeout` late, never
   * early — plus sub-millisecond overshoot on a fractional `timeout`. Both are
   * bounded and neither is the unbounded overshoot this bound used to carry.
   */
  maxAttempts: number
  /**
   * Milliseconds of wall clock before the client gives up, and the whole of it
   * is spent.
   *
   * That second clause is load-bearing rather than decorative. COM-871's first
   * pass bounded the chain at the ticks landing strictly *before* this, which
   * cut the real budget to the last grid tick — 55500 against an authored
   * 60000 at the fleet default, and 30500 against an authored 60000 at
   * `pollingTime: 30000`. `nextPollDelay` shortens the last delay so the
   * closing look lands on this number exactly: nothing is issued past it, and
   * nothing of it is left unspent.
   *
   * The one authored budget, and the thing every other bound is derived from
   * — which is exactly why it is the one that has to be bounded itself. Too
   * small to afford even the opening look and it is **rejected**; above
   * `ORDER_POLL_MAX_TIMEOUT_MS` it is **clamped**, because a long deadline is
   * a legitimate instruction overridden by policy rather than an unusable
   * value. See `resolveWithIssues` for both, and that constant for why
   * capping this bounds `maxAttempts` along with it.
   */
  timeout: number
  /**
   * `OrderStatus` values that mean the order settled successfully.
   *
   * Consumed by `deriveOrderOutcome`, **not** by `isOrderPending`. The two
   * answer different questions and one cannot be derived from the other: the
   * predicate asks whether the backend is still working on the order, these
   * lists say which statuses are final. Feeding them into the predicate would
   * make a bare `CREATED` order — one nothing may ever confirm — read as
   * pending and poll to the deadline, at the cost of a `completeBundlesExtra`
   * lookup per tick.
   *
   * Defaults to `DEFAULT_SUCCESS_STATUSES`.
   *
   * `readonly` for the reason `IOrderConfirmationConfigIssue.effective` gives:
   * the fallback is handed out by identity, and a mutable reference to it is a
   * reference to the list every unconfigured storefront polls against.
   */
  successStatuses: readonly string[]
  /**
   * `OrderStatus` values that mean the order settled as a failure.
   *
   * As `successStatuses`, and checked before it — see `deriveOrderOutcome`.
   * Defaults to `DEFAULT_FAILURE_STATUSES`. `readonly` as `successStatuses`.
   */
  failureStatuses: readonly string[]
}

/**
 * The success statuses an unconfigured storefront gets.
 *
 * `CONFIRMED` alone: it is the only status the confirmation path itself
 * produces — `confirmOrder` sets it natively, and `validateExternalRef` sets
 * it when a callback lands. Every other member of the enum is a later stage of
 * fulfilment (`SENT`, `SHIPPED`, `DELIVERED`, `PAID` …) which an order reaches
 * long after the shopper has left this screen, so including one by default
 * would only widen what counts as settled without any order ever arriving in
 * it here.
 * @internal
 */
export const DEFAULT_SUCCESS_STATUSES: readonly string[] = ['CONFIRMED']

/**
 * The failure statuses an unconfigured storefront gets.
 *
 * `ERROR` is what `markOrderAsError` and the order-error paths set; `CANCELLED`
 * is what `cancelOrder` sets. Both are terminal and neither is recoverable by
 * waiting, which is the whole test for membership here.
 *
 * `SUGGESTED` is deliberately absent even though it means the client system
 * changed the order: it is a *confirmable* status — `confirmOrder`'s filter
 * accepts it beside `CREATED` — so calling it a failure would abandon an order
 * one round trip from being confirmed.
 * @internal
 */
export const DEFAULT_FAILURE_STATUSES: readonly string[] = [
  'ERROR',
  'CANCELLED',
]

/**
 * The `ExternalRefStatus` values that mean the client system refused the order.
 *
 * Not configurable, and deliberately so. `successStatuses`/`failureStatuses`
 * exist because a storefront's *fulfilment* vocabulary is its own business —
 * whether `PROCESSED` counts as settled is a tenant question. These three are
 * not: they are the whole non-success half of the backend's `ExternalRefStatus`
 * enum (`PROCESSING`, `CONFIRMED`, `REJECTED`, `ERROR`, `TIMEOUT`), and there
 * is no reading of "the client system reported REJECTED" under which the
 * shopper should be told the order went through.
 *
 * `PROCESSING` is absent because it is the pending value, handled by
 * `isOrderPending`; `CONFIRMED` is absent because it is the success value and
 * arrives alongside `status: CONFIRMED` anyway.
 * @internal
 */
export const EXTERNAL_REF_FAILURE_STATUSES: OrderExternalRefStatus[] = [
  'REJECTED',
  'ERROR',
  'TIMEOUT',
]

/**
 * Whether the storefront splits orders server-side, and so forbids this flow.
 *
 * A mirror of the storefront's `isOrderSplit`: `configuration.splitters.order`
 * active **and** a non-zero `maxProduct`. A splitter authored with
 * `maxProduct: 0` splits nothing and the server reads it as off, so reading
 * `active` alone would refuse a storefront that is in fact fine.
 *
 * This exists because `createOrder` and `confirmOrder` do not degrade for a
 * splitting storefront — they `throw ERROR.BAD_REQUEST` immediately, naming
 * `createOrders`/`confirmOrders` instead — so an in-webview attempt fails on
 * every try, at the CTA, reported to the shopper as a generic creation
 * failure with no checkout rules named. Cinnamon does not implement the plural
 * mutations, so the honest answer is to decline the opt-in and take the legacy
 * path, which works for these storefronts today.
 *
 * Named for what it asks rather than what it gates, because the pair of
 * mutations is not the only thing that will ever care.
 * @internal
 */
export const isOrderSplitStorefront = (
  session?: SessionDataArgs | null,
): boolean => {
  const { active, maxProduct } = session?.configuration?.splitters?.order ?? {}
  return Boolean(active) && (maxProduct ?? 0) !== 0
}

/**
 * A non-empty list of strings, or the fallback.
 *
 * An empty array is treated as unconfigured rather than as "nothing is
 * terminal". The latter reading is never what an author means and its failure
 * is silent: every order would poll to its deadline and report an outcome
 * nobody could act on. It is no longer *entirely* silent — `resolveWithIssues`
 * reports it, keyed on this function returning the fallback by identity — but
 * the reinterpretation still happens and this is still where it happens.
 *
 * **The non-array guard is live, but not for the reason it looks like.** A
 * genuine non-array cannot arrive: the SDL types these `[String]`, so output
 * serialization raises `Expected Iterable`, nullifies the field, and Apollo's
 * default `errorPolicy` then discards the whole response — see
 * `positiveNumber` below for that mechanism. What does arrive is `null`, and
 * routinely: GraphQL renders an absent field as `null` rather than omitting
 * it, so a block authoring only `required` sends `successStatuses: null` and
 * `!Array.isArray(null)` is this branch. It is the same partial-authoring
 * case that `OrderConfirmationSchema`'s `default: undefined` exists for
 * storefront-side. The per-member `typeof status === 'string'` filter is the
 * defensive half — the SDL and joi both constrain members to strings.
 */
const statusList = (
  value: unknown,
  fallback: readonly string[],
): readonly string[] => {
  if (!Array.isArray(value)) return fallback
  const statuses = value.filter(
    (status): status is string =>
      typeof status === 'string' && status.length > 0,
  )
  return statuses.length ? statuses : fallback
}

/**
 * A positive, finite number, or the fallback.
 *
 * ⚠️ **These branches differ in whether they can fire through the wire, and
 * the ones that read as the point of the function are the defensive ones.**
 * Once Half A is serving, the SDL types these fields `Int` and joi constrains
 * them to non-negative integers, so scalar serialization sits in *front* of
 * this function, not behind it:
 *
 * - **The non-scalar branch is live and routine.** A partially authored block
 *   — `required: true` with no `pollingTime` — arrives as `pollingTime: null`,
 *   because GraphQL renders an absent field as `null` rather than omitting
 *   it. `typeof null` is `'object'`, so this is the branch every opted-in
 *   storefront that omits a key takes. Not defensive.
 * - **Rejecting `0` and negatives is live.** joi permits `0` deliberately
 *   (`nonNegativeIntegerConstraint`), so that an authoring slip stays inert
 *   in the consumer instead of becoming a write rejection in another system.
 *   That leaves this the only thing stopping a zero interval, which is a
 *   tight loop against the API with a `completeBundlesExtra` lookup per call.
 * - **Accepting numeric strings is defensive.** `"5000"` cannot arrive as a
 *   string — `GraphQLInt` coerces it to `5000` before cinnamon sees it — so
 *   read this branch as defence for a caller that is not this wire (a direct
 *   Mongo write, which is real here: COM-765) and for the tests, which pass
 *   raw strings through `as never`. Kept, but it is not what protects
 *   production.
 * - **`"soon"` never reaches this function**, and the mechanism is worth
 *   stating exactly, because "the server rejects it" is wrong. `Int`
 *   serialization fails on that one field; `pollingTime` is nullable, so
 *   graphql-js nullifies *just it* and still returns the rest of `data` with
 *   a partial `errors` entry. What throws the response away is the client —
 *   cinnamon's Apollo client sets no `errorPolicy`, so the default `'none'`
 *   discards `data` wholesale. The cost of a `"soon"` in Mongo is a failed
 *   boot, not a silent fallback.
 *
 * Verified against graphql-js 16.13.2 and the Half A SDL rather than assumed.
 */
const positiveNumber = (value: unknown, fallback: number): number => {
  if (typeof value !== 'number' && typeof value !== 'string') return fallback
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback
}

/**
 * The poll interval: read as a positive number, then raised to
 * `ORDER_POLL_MIN_INTERVAL_MS` if it falls below it.
 *
 * The two steps are ordered, and the order is the whole design. A value that
 * is not positive at all — `0`, a negative, `null`, `"soon"` — is *absent* as
 * far as this file is concerned, so it takes `ORDER_POLL_INTERVAL_MS` and is
 * never clamped; clamping it would silently convert a broken value into a
 * working one five times faster than the default.
 *
 * Those four are not four live populations, and the list should not be read
 * as one: `0` arrives because joi permits it and `null` because GraphQL
 * renders an omitted key that way, a negative takes a direct Mongo write to
 * get past joi's `min(0)`, and `"soon"` never arrives at all — see
 * `positiveNumber` above. A value that *is* positive
 * but below the floor is a real instruction, honoured as closely as allowed.
 * So `0` resolves to 5000 and `5` resolves to 1000, and those two outcomes
 * differ on purpose.
 *
 * See `ORDER_POLL_MIN_INTERVAL_MS` for why the floor exists at all, and
 * `derivePollAttempts` for the different answer `maxAttempts` and `timeout`
 * got in COM-871 — a derivation and a reachability test rather than floors.
 */
const pollingInterval = (value: unknown): number =>
  Math.max(
    positiveNumber(value, ORDER_POLL_INTERVAL_MS),
    ORDER_POLL_MIN_INTERVAL_MS,
  )

/**
 * How many poll ticks a wall-clock budget affords at a given cadence.
 *
 * **Derive this; do not copy `Math.ceil(timeout / pollingTime)` from
 * anywhere.** That expression was the obvious form and it is wrong, because
 * the tick grid is not `P, 2P, 3P`. Since COM-875 the tick that *opens* a
 * chain fires at the fixed `ORDER_POLL_FIRST_LOOK_MS` and only the ticks after
 * it use `pollingTime`, so grid tick `k` lands at `F + (k - 1) * P`. The
 * largest `k` landing strictly before the deadline is `ceil((T - F) / P)`, not
 * `ceil(T / P)` — the two agree for most round numbers, which is what makes
 * the wrong one so easy to ship, and part company exactly when a multiple of
 * `P` falls in `[T - F, T)` — closed at the bottom and open at the top, which
 * is not a detail: at `T = 1000`, `P = 1000` the two agree, and at `T = 1500`,
 * `P = 1000` they differ, and the half-open interval the other way round
 * predicts both backwards. Note the arithmetic no longer separates the two
 * formulas at `T = 5200`, `P = 5000` — the grid affords one tick and the
 * closing look makes two, which is what `ceil(T / P)` also answers. The
 * example still shows what the copied formula gets wrong: it puts that second
 * tick at 5500, three hundred milliseconds past a deadline the storefront
 * authored, where `nextPollDelay` puts it on 5200.
 *
 * **Plus one for the closing look, and that `+ 1` is the whole of COM-871's
 * correction.** The grid count alone was shipped as the attempt bound on the
 * strength of a claim that turned out to be false: that the tick landing at or
 * past the deadline was "one trailing request the deadline check would throw
 * away". It is not thrown away. In both hooks `pollTick` dispatches its result
 * and settles the order *before* it evaluates the budget — the budget check
 * gates only the scheduling of the **next** tick — so that trailing tick was
 * the chain's last look at the order, and bounding the chain at the grid count
 * deleted it. Measured at the fleet default (60 s / 5 s) the last look moved
 * from 60500 to 55500, so an order settling in that five-second window
 * reported `unknown` — terminal and unretryable on the write surface — instead
 * of `confirmed`; at `pollingTime: 30000, timeout: 60000` the client gave up
 * at 30.5 s on a budget authored for 60.
 *
 * So the chain now spends its whole deadline: when the next grid tick would
 * overshoot `T`, `nextPollDelay` shortens it to land *on* `T` instead of
 * stopping. Ticks are therefore `F, F + P, …` for as long as they fall
 * strictly before `T`, and then one closing look at exactly `T` — which is
 * where the `+ 1` comes from, and why `maxAttempts` **counts** that look. Both
 * bounds still fire on the same tick: the closing look reaches
 * `elapsed >= timeout` and `attempts >= maxAttempts` together, by
 * construction.
 *
 * Do **not** read that as each bound earning its place on the grid — the
 * opposite follows. Because they fire together, reverting `maxAttempts` to
 * `Infinity` changes no observable behaviour anywhere in the suite (measured:
 * six killers, every one of them arithmetic in this file's own tests). Each is
 * observable only where the other cannot fire: the attempt bound against an
 * *authored* `maxAttempts` that binds early, the deadline against latency,
 * where a slow request spends the clock ahead of the grid.
 *
 * **"Strictly before" survives, but only as a statement about the grid.** It
 * is no longer an argument for stopping early — that argument rested on the
 * false premise above. It is the counting rule for the ticks `pollingTime`
 * schedules, and the closing look is added to it because `pollTick` tests
 * `elapsed >= timeout` *after* dispatching, so a request that lands exactly on
 * the deadline is read and acted on. Nothing is ever issued *past* the
 * deadline; that is the property the shortened delay buys, and it is what the
 * grid count alone was reaching for by the wrong route.
 *
 * **Precondition: `pollingTime > 0`.** Exported, and `derivePollAttempts(60000,
 * 0)` is `Infinity` — the unbounded budget this ticket removed, handed straight
 * back. `resolveWithIssues` is the only production caller and reads the value
 * through `pollingInterval`, which floors it at `ORDER_POLL_MIN_INTERVAL_MS`,
 * so the precondition holds there by construction rather than by check. A
 * second caller has to establish it for itself.
 *
 * **Below the opening look this goes to 1 or less, and it is not the rejection
 * test.** (One when `pollingTime > ORDER_POLL_FIRST_LOOK_MS`, which every
 * resolved interval is; the grid term goes negative for a smaller one, and
 * only a caller that bypasses `pollingInterval` can supply that.)
 * `T <= F` leaves no grid tick at all and the count falls to the bare closing
 * look — which cannot be scheduled either, since only a completed tick
 * schedules it and the opening look at `F` already reaches
 * `elapsed >= timeout`. The rejection lives in `resolveWithIssues` and is
 * written directly against `ORDER_POLL_FIRST_LOOK_MS`; see it for why.
 *
 * **It is a scheduling bound, not a guarantee about elapsed time.** Real
 * requests take time and nothing bounds an individual one while COM-772 is
 * open, so the ticks land later than this grid and fewer of them fit. That is
 * why the wall-clock check stays: it is the backstop for latency, and it is
 * the only thing that can still end a chain early.
 * @internal
 */
export const derivePollAttempts = (
  timeout: number,
  pollingTime: number,
): number => Math.ceil((timeout - ORDER_POLL_FIRST_LOOK_MS) / pollingTime) + 1

/**
 * How long until the next tick, shortened so the last one lands *on* the
 * deadline rather than past it (COM-871).
 *
 * The scheduling half of `derivePollAttempts`, and written here beside it
 * rather than at the two `pollTick` tails so the grid and the bound derived
 * from it cannot drift apart — the closing look the `+ 1` counts is the one
 * this returns.
 *
 * `elapsed` must be the **same sample** `pollTick` tested its deadline
 * against, not a fresh `Date.now()`. That is what makes the result strictly
 * positive with no clamp: the caller reaches here only after
 * `elapsed >= timeout` returned false, so `timeout - elapsed > 0` — strictly,
 * for any real `timeout`, which is why no integrality is needed here. Take a
 * second sample instead and the clock can advance between the two, which is
 * the one way this returns `0` rather than a positive remainder. That costs a
 * closing look fired immediately rather than on the deadline, which then hits
 * `elapsed >= timeout` and terminates — the appearance of a bug more than a
 * bug. No `Math.max(0, …)` guards it either: a clamp reachable
 * only through a bug in the caller reads as a live guard while protecting
 * nothing, the same reasoning `ORDER_POLL_FIRST_LOOK_MS` gives for having no
 * `Math.min` against `pollingTime`.
 *
 * Under latency this can be very short — a request that resolves one
 * millisecond inside the deadline buys a closing look one millisecond later.
 * That is the authored budget being spent, not an overrun: the request is
 * issued at `T` and never after it, where the pre-COM-871 code issued it at
 * `elapsed + P`, arbitrarily far past.
 * @internal
 */
export const nextPollDelay = (
  pollingTime: number,
  timeout: number,
  elapsed: number,
): number => Math.min(pollingTime, timeout - elapsed)

/**
 * What went wrong with one authored field, and in which of three ways.
 *
 * Kept as three distinguishable reasons rather than one "misconfigured" line
 * because they need three different responses:
 *
 * - `rejected` — the value cannot be used at all, so the shipped default is in
 *   force and the authored one does nothing.
 * - `clamped` — the value is **legitimate** and was overridden by a policy of
 *   this client's rather than by anything wrong with it. The report has to say
 *   so, or an author reads it as "your value was bad" and changes the wrong
 *   thing.
 * - `emptyList` — an author wrote a list and meant an instruction by it; a
 *   list with nothing usable in it is read as *unconfigured*, which is the
 *   opposite of what was asked for.
 * @internal
 */
export type OrderConfirmationIssueReason = 'rejected' | 'clamped' | 'emptyList'

/**
 * One field's disagreement between what was authored and what is in force.
 * @internal
 */
export interface IOrderConfirmationConfigIssue {
  field:
    | 'pollingTime'
    | 'maxAttempts'
    | 'timeout'
    | 'successStatuses'
    | 'failureStatuses'
  reason: OrderConfirmationIssueReason
  /**
   * As authored, so the report can name it rather than describe it.
   *
   * `NonNullable<unknown>` and not `unknown`, which is a type doing a job the
   * convention was doing alone. Every `note()` sits inside an `isAuthored`
   * guard today, so an `undefined` here is unreachable — but the type did not
   * know that, and the failure if one ever arrived is silent rather than loud:
   * `JSON.stringify(undefined)` returns the *value* `undefined`, so the Datadog
   * message would interpolate the bare word `undefined` and the `extraInfo`
   * key would be dropped from the context object entirely. Widening the guard
   * to `NonNullable` makes noting a field outside it a compile error, and
   * narrows `authoredInterval`, `authoredTimeout` and `authoredAttempts` at
   * their call sites as a bonus.
   */
  authored: NonNullable<unknown>
  /**
   * What the poll actually runs on.
   *
   * `readonly` because a list arrives here as `DEFAULT_SUCCESS_STATUSES` or
   * `DEFAULT_FAILURE_STATUSES` *by identity* — the report is keyed on exactly
   * that — so handing a caller a mutable reference to the module's own
   * defaults would let it edit the fallback every unconfigured storefront
   * takes. Copying instead is not the fix: the copy breaks the `list ===
   * fallback` test that distinguishes "the fallback was taken" from "the
   * authored list happens to equal it".
   */
  effective: number | readonly string[]
}

/**
 * Whether the storefront said anything about this field at all.
 *
 * **`null` is not authored, and this is the whole difficulty of the report.**
 * GraphQL renders an omitted field as `null` rather than dropping it, so a
 * storefront authoring the ordinary `{ required: true }` sends `pollingTime`,
 * `maxAttempts`, `timeout`, `successStatuses` and `failureStatuses` all as
 * `null`. Every one of them is then "refused" by `positiveNumber`/`statusList`
 * and replaced by a default — correctly, silently, and on very nearly every
 * opted-in storefront there will ever be.
 *
 * The `recommendationLimits` predicate shape (`value !== undefined &&
 * !isUsable(value)`) is therefore **not** transferable here: `null !==
 * undefined`, so it would fire five times for the commonest correct block in
 * the estate and drown the signal this exists to carry.
 */
const isAuthored = (value: unknown): value is NonNullable<unknown> =>
  value !== undefined && value !== null

/**
 * Sentinel for "`positiveNumber` refused this".
 *
 * Safe as a fallback because `positiveNumber` never *returns* `0`: it yields
 * either a finite value greater than zero or the fallback it was handed. So a
 * `0` back from it means the branch was taken, with no second predicate to
 * drift from the first.
 */
const UNUSABLE = 0

/**
 * Resolves the block and records every disagreement it found on the way.
 *
 * One pass rather than a resolver plus an independent audit. The audit has to
 * name the value actually in force, and a second implementation of "what is in
 * force" is a second answer to one question — the trap `isOrderPending` was
 * extracted to avoid. Everything reported here is read off the same locals the
 * returned config is built from.
 *
 * **Which of these can actually fire, stated plainly**, because a report whose
 * reasons are mostly unreachable reads as several measured populations when it
 * is one:
 *
 * - Live through ordinary Admin API authoring, since joi types these `Int` and
 *   permits `0` deliberately: `pollingTime: 0`, `maxAttempts: 0`, `timeout: 0`,
 *   a `timeout` of 1–500, a `timeout` above `ORDER_POLL_MAX_TIMEOUT_MS`, a
 *   `pollingTime` of 1–999, an authored `[]` or a list whose members are all
 *   blank, and a `maxAttempts` above what the budget affords.
 * - Live only through a direct Mongo write, which is real here (COM-765): a
 *   negative number.
 * - **Not reachable at all, and so not reported:** a non-array status list and
 *   a non-numeric number. Both fail GraphQL serialization on the field, and
 *   Apollo's default `errorPolicy: 'none'` then discards the whole response —
 *   a failed boot, not a value arriving here to be judged. The `statusList`
 *   and `positiveNumber` guards that catch those shapes are still load-bearing
 *   for `null`, which is the ordinary path; do not read their presence as
 *   evidence that a report belongs beside them.
 */
const resolveWithIssues = (
  config?: IOrderConfirmationConfig,
): {
  resolved: IResolvedOrderConfirmation
  issues: IOrderConfirmationConfigIssue[]
} => {
  const issues: IOrderConfirmationConfigIssue[] = []
  const note = (
    field: IOrderConfirmationConfigIssue['field'],
    reason: OrderConfirmationIssueReason,
    authored: NonNullable<unknown>,
    effective: number | readonly string[],
  ) => {
    issues.push({ field, reason, authored, effective })
  }

  const authoredInterval = config?.pollingTime
  const pollingTime = pollingInterval(authoredInterval)
  if (isAuthored(authoredInterval)) {
    const usable = positiveNumber(authoredInterval, UNUSABLE)
    if (!usable) note('pollingTime', 'rejected', authoredInterval, pollingTime)
    else if (usable !== pollingTime)
      note('pollingTime', 'clamped', authoredInterval, pollingTime)
  }

  /*
   * A budget affording no look is unusable rather than short, so it falls back
   * the way a `0` does instead of being honoured as closely as allowed. There
   * is no honourable approximation of "give up before looking".
   *
   * **Written against the constant, not against `derivePollAttempts`.** Until
   * COM-871's second pass this read `derivePollAttempts(requested, pollingTime)
   * > 0`, on the argument that routing the boundary through the arithmetic made
   * it derived rather than chosen. The arithmetic no longer says that: the
   * count now includes the closing look, so it is `1` for a budget of `300` and
   * the equivalence it was trusted for is gone. Stating the rule directly is
   * both true and shorter to check — every chain opens with a look armed at the
   * hardcoded `ORDER_POLL_FIRST_LOOK_MS`, which `timeout` neither pays for nor
   * moves, so a budget at or below it buys nothing that lands inside it. The
   * opening look at `F` reaches `elapsed >= timeout` on its own tick and no
   * closing look is ever scheduled, because only a tick whose budget check
   * passed schedules one.
   *
   * A `requestedTimeout > 0` operand was here too and is gone with it:
   * `positiveNumber` returns `0` for everything it refuses, and `0 > 500` is
   * already false. It was measured as an equivalent mutant under the old test
   * as well: the grid term at `T = 0` is `ceil(-500 / P)`, which is `-0` for
   * any `P >= 500` and negative below it — non-positive either way, so the
   * operand could never change a result in either form.
   */
  const authoredTimeout = config?.timeout
  const requestedTimeout = positiveNumber(authoredTimeout, UNUSABLE)
  const affordsALook = requestedTimeout > ORDER_POLL_FIRST_LOOK_MS
  // The ceiling is applied after the reachability test rather than before it.
  // The two cannot both fire — a budget affording no look is at most
  // `ORDER_POLL_FIRST_LOOK_MS`, three orders of magnitude under the ceiling —
  // so the order encodes which question is asked first: "is this usable at
  // all" before "is it inside our policy", which is the same order the two
  // reasons are named in.
  const timeout = affordsALook
    ? Math.min(requestedTimeout, ORDER_POLL_MAX_TIMEOUT_MS)
    : ORDER_POLL_TIMEOUT_MS
  if (isAuthored(authoredTimeout)) {
    if (!affordsALook) note('timeout', 'rejected', authoredTimeout, timeout)
    // Read off the clamp itself rather than re-testing against the ceiling, so
    // the report cannot drift from what is in force — the same shape the
    // `pollingTime` floor above uses.
    else if (requestedTimeout !== timeout)
      note('timeout', 'clamped', authoredTimeout, timeout)
  }

  // Derived from the two values above rather than read beside them, so the
  // attempt bound cannot claim a window the deadline does not grant.
  const affordedAttempts = derivePollAttempts(timeout, pollingTime)
  const authoredAttempts = config?.maxAttempts
  const requestedAttempts = positiveNumber(authoredAttempts, UNUSABLE)
  const maxAttempts = requestedAttempts
    ? Math.min(requestedAttempts, affordedAttempts)
    : affordedAttempts
  if (isAuthored(authoredAttempts)) {
    if (!requestedAttempts)
      note('maxAttempts', 'rejected', authoredAttempts, maxAttempts)
    // Read off the value in force rather than re-deriving the bound, for the
    // reason the `timeout` block above states: a second test of "was this
    // overridden" is a second answer to the question the resolved value has
    // already settled. `requestedAttempts > affordedAttempts` was that second
    // answer and it is gone.
    else if (requestedAttempts !== maxAttempts)
      note('maxAttempts', 'clamped', authoredAttempts, maxAttempts)
  }

  // `statusList` returns the fallback *by identity* when nothing usable
  // survives its filter, which is exactly the "authored and read as
  // unconfigured" case — and it covers `[""]` as well as `[]`, since a blank
  // member is filtered out before the length test.
  const statuses = (
    field: 'successStatuses' | 'failureStatuses',
    authored: unknown,
    fallback: readonly string[],
  ): readonly string[] => {
    const list = statusList(authored, fallback)
    if (Array.isArray(authored) && list === fallback)
      note(field, 'emptyList', authored, list)
    return list
  }

  return {
    resolved: {
      // Exactly `true`. Remote JSON has no boolean guarantee, and a quoted
      // `"false"` is truthy — the same trap `packages.sort.enabled` documents.
      isAsync: config?.required === true,
      pollingTime,
      maxAttempts,
      timeout,
      successStatuses: statuses(
        'successStatuses',
        config?.successStatuses,
        DEFAULT_SUCCESS_STATUSES,
      ),
      failureStatuses: statuses(
        'failureStatuses',
        config?.failureStatuses,
        DEFAULT_FAILURE_STATUSES,
      ),
    },
    issues,
  }
}

/**
 * Reads `configuration.order.confirmation` into the shape the poll uses.
 *
 * An absent block is the common case — it means the storefront has not opted
 * in — and it resolves to COM-741's own constants, with the one exception
 * COM-871 introduced: `maxAttempts` is the derived ceiling rather than
 * `Infinity`, so an unconfigured chain now stops on the tick that lands *on*
 * its deadline instead of on the first one past it. Thirteen ticks either way
 * at the fleet default; the last of them moves from 60500 to 60000.
 */
export const resolveOrderConfirmation = (
  config?: IOrderConfirmationConfig,
): IResolvedOrderConfirmation => resolveWithIssues(config).resolved

/**
 * Every field whose authored value is not the one in force.
 *
 * Reported by `useOrderConfirmationConfigGuards`, never thrown, for the reason
 * `getUnknownOrderStatuses` gives. Empty for an absent block and — the case
 * that matters most — empty for the ordinary `{ required: true }` block whose
 * other five fields arrive as `null`; see `isAuthored`.
 */
export const getOrderConfirmationConfigIssues = (
  config?: IOrderConfirmationConfig,
): IOrderConfirmationConfigIssue[] => resolveWithIssues(config).issues

/**
 * Configured statuses that are not `OrderStatus` members.
 *
 * A guard, as of COM-776, and no longer merely a lint. `successStatuses` and
 * `failureStatuses` now reach `deriveOrderOutcome`, which is what decides
 * whether an order has settled — so a typo'd member is a status the derivation
 * can never match, and an order that reaches it polls to its deadline and then
 * reports a timeout it never had. That is the failure this names, and it is
 * silent without this check because a misspelt status is indistinguishable
 * from one the order simply has not reached yet.
 *
 * Note what it does not cover: a status listed under **both** keys. That is
 * authoring nonsense rather than a typo, every member of it is a real enum
 * value, and `deriveOrderOutcome` resolves it deterministically by checking
 * failures first.
 *
 * The caller reports these to Datadog rather than throwing — the screen still
 * works, and taking it down over a config typo would be the larger outage.
 */
export const getUnknownOrderStatuses = (
  config?: IOrderConfirmationConfig,
): string[] => {
  const configured = [
    ...(Array.isArray(config?.successStatuses) ? config.successStatuses : []),
    ...(Array.isArray(config?.failureStatuses) ? config.failureStatuses : []),
  ]
  return configured.filter(
    (status) =>
      !ORDER_STATUSES.includes(status as (typeof ORDER_STATUSES)[number]),
  )
}

/**
 * Whether the block was authored but will not switch the screen on.
 *
 * `required` is compared against literal `true` on purpose — remote JSON has
 * no boolean guarantee and a quoted `"false"` is truthy — but the cost of that
 * strictness is silent. A storefront that authors `successStatuses`,
 * `pollingTime` and `timeout` while writing `required: "true"`, or while
 * omitting `required` altogether, gets `isAsync: false`: the shopper is shown
 * a still-`PROCESSING` order as though it were final, and nothing anywhere
 * says so. It is the likelier of the two authoring mistakes this file guards
 * against — a typo'd status at least has to be typed.
 *
 * Three things are deliberately not this:
 * - an **absent** block, which is every storefront that has not opted in;
 * - an **empty** one, where nothing was authored to contradict;
 * - a literal `required: false`, which is an explicit opt-out and a decision
 *   rather than a slip, even beside a fully authored block.
 *
 * Reported by the caller, never thrown, for the reason
 * `getUnknownOrderStatuses` gives.
 */
export const isRequiredMisauthored = (
  config?: IOrderConfirmationConfig,
): boolean => {
  if (!config || typeof config !== 'object') return false
  if (!Object.keys(config).length) return false
  return config.required !== true && config.required !== false
}

/**
 * Whether the client's backend is still working on this order.
 *
 * Two pending shapes, and neither field alone covers both.
 *
 * `externalRefStatus === 'PROCESSING'` is the external-ref path. Testing
 * `status === 'CREATED'` instead would be wrong: it also covers orders that
 * were never submitted anywhere, so the screen would tick at something that is
 * never going to move — and every tick costs a `completeBundlesExtra` lookup
 * server-side.
 *
 * `status === 'WAITING_CALLBACK'` is the callback path, and it is only
 * expressible on `status`. The CREATE_ORDER add-on sets it when it has
 * `hasCallback`, before any external ref exists; `validateExternalRef` sets
 * `externalRefStatus` only once the callback lands. So a callback-path order
 * carries no `externalRefStatus` while it waits, and testing that field alone
 * would never poll it at all.
 *
 * Shared by the poll, which decides whether to tick again, and by the panel
 * status selector, which decides whether to say "confirming". Two copies of
 * this predicate would be two answers to the same question.
 */
export const isOrderPending = (order?: IOrderDetail | null): boolean =>
  order?.externalRefStatus === 'PROCESSING' ||
  order?.status === 'WAITING_CALLBACK'

/**
 * Where an order stands, as the in-webview confirmation flow reads it.
 *
 * Three terminal members and one that is not. The names are the same words
 * `IOrdersState.creationStatus` uses, so the flow can dispatch what this
 * returns without a translation table in between — and so the confirmation
 * screen, which reads the slice, and the poll, which reads this, cannot drift
 * into two vocabularies for one fact.
 *
 * `creating`, `confirming` and `unknown` are absent because they are not
 * properties of an order: the first two are requests in flight and the third
 * is the client having given up. Those live on the slice.
 * @internal
 */
export type OrderOutcome = 'pending' | 'created' | 'confirmed' | 'failed'

/**
 * Whether an order has settled, and how.
 *
 * The single place terminality is decided. Written here rather than in the
 * flow hook because its other consumer is the confirmation screen, which
 * renders the outcome and must reach the same verdict the poll reached when it
 * stopped ticking — two copies would be two answers to one question, which is
 * the trap `isOrderPending` was extracted to avoid.
 *
 * The order of the five checks is the whole content of this function, and the
 * two fields have to be read together because the backend moves them
 * independently:
 *
 * 1. **A terminal `externalRefStatus` outranks everything, `status`
 *    included.** `validateExternalRef` — the callback handler — assigns
 *    `order.externalRefStatus = externalRefStatus || CONFIRMED` and then calls
 *    `updateOrderAndSendEvent` with `status: OrderStatus.CONFIRMED`
 *    **unconditionally**, with no branch on the value the client system
 *    reported. So a client that calls back with `REJECTED` (or `ERROR`, or
 *    `TIMEOUT` — joi accepts all three) produces
 *    `{ status: CONFIRMED, externalRefStatus: REJECTED }`. Reading `status`
 *    first would tell the shopper a rejected order succeeded, which by check
 *    2's own reasoning is the expensive direction.
 * 2. **Failures before successes.** A status in both lists is authoring
 *    nonsense that `getUnknownOrderStatuses` cannot catch, so it has to resolve
 *    one way; failure is the safer reading. Telling a shopper an order failed
 *    when it succeeded costs them a phone call, telling them it succeeded when
 *    it failed costs them a delivery they will wait for.
 * 3. **A reached terminal status beats `isOrderPending`.** This is the
 *    ordering COM-776 got wrong and shipped nowhere: the pending predicate
 *    used to run first and be unoverridable, which made a confirmed order on
 *    an add-on storefront unreportable. `createOrderAddon` writes
 *    `{ $set: { externalRefStatus: PROCESSING } }` unconditionally, before its
 *    `hasCallback` branch; `externalRefStatus` has no schema default and
 *    `GenericModel.update` merges only the keys the update object carries, so
 *    the value survives every later write; and the native `confirmOrder` sets
 *    `status: CONFIRMED` without touching `externalRefStatus` — a repo-wide
 *    search finds exactly three writers, the two create-order add-ons and the
 *    callback handler. A confirmed order on such a storefront is therefore
 *    `{ status: CONFIRMED, externalRefStatus: PROCESSING }` for good, and
 *    pending-first burnt the whole `maxAttempts`/`timeout` budget to report
 *    `unknown`. Letting the configured lists win costs nothing the guards do
 *    not already cover: `WAITING_CALLBACK` and a bare `CREATED` are in neither
 *    default list, so both still fall through to the predicate.
 * 4. **`isAsync` decides what a merely-`CREATED` order means.** Where the
 *    storefront declared no asynchronous confirmation, nothing is going to
 *    move this order on the shopper's timescale and `created` is the honest
 *    terminal answer — the order was placed and the store will confirm it.
 *    Where it did, the same status means confirmation has not landed yet, so
 *    the safety net keeps polling.
 * 5. Everything else is `pending`, bounded by `maxAttempts` and `timeout`.
 *
 * An absent order reads as `pending`: the flow has nothing to settle on yet,
 * and `created` — the only other candidate for "no evidence" — would announce
 * an order that may not exist.
 */
export const deriveOrderOutcome = (
  order: IOrderDetail | null | undefined,
  config?: IOrderConfirmationConfig,
): OrderOutcome => {
  if (!order) return 'pending'

  const { isAsync, successStatuses, failureStatuses } =
    resolveOrderConfirmation(config)
  const status = order.status ?? ''
  const { externalRefStatus } = order

  // Guarded on presence rather than defaulted to `''`: an empty string is not
  // an `ExternalRefStatus` and using one as a sentinel invites it being
  // compared against a real value somewhere later.
  if (
    externalRefStatus &&
    EXTERNAL_REF_FAILURE_STATUSES.includes(externalRefStatus)
  )
    return 'failed'
  if (failureStatuses.includes(status)) return 'failed'
  if (successStatuses.includes(status)) return 'confirmed'
  if (isOrderPending(order)) return 'pending'
  if (!isAsync) return 'created'
  return 'pending'
}

export default resolveOrderConfirmation
